import { BindableValue } from 'record-it-utils/misc/BindableValue/BindableValue';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { PromiseContainer } from 'record-it-utils/utils/PromiseContainer/PromiseContainer';
import { ScopeBasedLock } from 'record-it-utils/utils/ScopeBasedLock/ScopeBasedLock';
import { TimingUtils } from 'record-it-utils/utils/TimingUtils/TimingUtils';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityWithEntityName } from '../EntityWithEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntitySynchronizationQueue<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  AfterItemUploadedReturnValue
> {
  private items: Array<
    QueueItem<TSynchronizationEnvironmentTypes, any, TEntityTypesByEntityName>
  > = [];
  private started: boolean = false;
  private bindableIsDraining = new BindableValue({ defaultValue: false });
  private drainingFinishedPromiseContainer: PromiseContainer<void> =
    new PromiseContainer();
  private scopeBasedLock = new ScopeBasedLock();

  constructor(
    private readonly orderedEntityNames: Array<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
    >,
    private readonly uploadFunction: SynchronizationQueueUploadFunction<
      TSynchronizationEnvironmentTypes,
      any,
      TEntityTypesByEntityName,
      AfterItemUploadedReturnValue
    >,
    private readonly afterItemUploaded: (
      uploadFunctionReturnValue: AfterItemUploadedReturnValue
    ) => void,
    private readonly afterItemFailedToUpload: () => void
  ) {}

  public start(): void {
    this.started = true;
    this.resetItems();
    this.startDrainingItems();
  }

  public stop(): void {
    this.started = false;
  }

  public addItems(
    items: Array<
      EntitySynchronizationInfo<
        TSynchronizationEnvironmentTypes,
        any,
        TEntityTypesByEntityName
      >
    >
  ): void {
    for (const item of items) {
      this.addItem(item);
    }
  }

  public addItem<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    item: EntitySynchronizationInfo<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  ): void {
    const itemIndex = this.findItemIndex(
      item.entityName,
      item.entity.id,
      false
    );

    const queueItem = {
      entityInfo: item,
      order: this.getOrderForEntityName(item.entityName),
      failed: false,
      isBeingUploaded: false,
      needsReupload: false
    };

    if (itemIndex >= 0) {
      if (
        queueItem.entityInfo.entity.onlyLocal &&
        queueItem.entityInfo.type === QueueItemType.DELETE
      ) {
        // don't synchronize the deletion of a local entity
        this.items.splice(itemIndex, 1);
      } else {
        this.items[itemIndex] = queueItem;
      }
    } else if (
      queueItem.entityInfo.type !== QueueItemType.DELETE ||
      !queueItem.entityInfo.entity.onlyLocal
    ) {
      this.insertItem(queueItem);
    }

    if (this.started) {
      this.startDrainingItems();
    }
  }

  public getItemsToSynchronizeInfos(): Array<
    EntityToSynchronizeInfo<TSynchronizationEnvironmentTypes>
  > {
    return this.items.map((item) => {
      return {
        entityId: item.entityInfo.entity.id,
        entityName: item.entityInfo.entityName,
        failed: item.failed
      };
    });
  }

  public getItemsToSave(): Array<
    EntitySynchronizationInfo<
      TSynchronizationEnvironmentTypes,
      any,
      TEntityTypesByEntityName
    >
  > {
    return this.items.map((i) => i.entityInfo);
  }

  public isDraining(): boolean {
    return this.bindableIsDraining.getValue();
  }

  public bindIsDraining(callback: (isDraining: boolean) => void): Disposable {
    return this.bindableIsDraining.bindValue(callback);
  }

  public async waitUntilAllItemsAreSynchronized(): Promise<void> {
    if (!this.started) {
      throw new Error(
        "can't waitUntilAllItemsAreSynchronized because the queue isn't started"
      );
    }

    if (
      this.items.find(
        (item) => !item.failed && item.entityInfo.entity.readyToSynchronize
      )
    ) {
      await this.drainingFinishedPromiseContainer.create();
    }
  }

  public entityIsIncluded(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    entityId: string
  ): boolean {
    return this.findItemIndex(entityName, entityId, true) >= 0;
  }

  public retryFailedItems(): void {
    if (this.items.find((item) => item.failed)) {
      this.resetFailedItems();
      this.startDrainingItems();
    }
  }

  public lock(scope: any): void {
    this.scopeBasedLock.lock(scope);
  }

  public unlock(scope: any): void {
    this.scopeBasedLock.unlock(scope);

    if (this.started) {
      this.startDrainingItems();
    }
  }

  public modifyQueueItems(
    modifyFunction: ModifyFunction<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): boolean {
    let modified = false;

    for (const item of this.items) {
      if (modifyFunction(item.entityInfo)) {
        modified = true;
        if (item.isBeingUploaded) {
          item.needsReupload = true;
        }
        this.retryFailedItem(item);
      }
    }

    return modified;
  }

  private retryFailedItem(
    item: QueueItem<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ): void {
    if (item.failed) {
      item.failed = false;
      if (this.started) {
        this.startDrainingItems();
      }
    }
  }

  private startDrainingItems(): void {
    // not awaiting this on purpose, so `startDrainingItems` won't get async
    // and we don't want that because awaiting `startDrainingItems` would make no sense
    void this.drainItems();
  }

  private async drainItems(): Promise<void> {
    if (this.bindableIsDraining.getValue() || this.scopeBasedLock.isLocked()) {
      return;
    }

    this.bindableIsDraining.setValue(true);

    for (;;) {
      const item = this.findNextItemToDrain();
      if (!item) {
        break;
      }

      try {
        item.isBeingUploaded = true;
        const uploadFunctionReturnValue = await TimingUtils.withTimeout(
          this.uploadFunction(item.entityInfo),
          5000
        );

        if (item.needsReupload) {
          item.isBeingUploaded = false;
          item.needsReupload = false;
        } else {
          this.removeItem(item);
        }
        this.afterItemUploaded(uploadFunctionReturnValue);
      } catch (e) {
        console.error(e);
        item.failed = true;
        item.isBeingUploaded = false;
        item.needsReupload = false;
        this.afterItemFailedToUpload();
      }

      if (this.scopeBasedLock.isLocked() || !this.started) {
        break;
      }
    }

    this.bindableIsDraining.setValue(false);
    this.drainingFinishedPromiseContainer.resolveAll();
  }

  private findItemIndex(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    entityId: string,
    includeItemsWhichAreBeingUploaded: boolean
  ): number {
    return this.items.findIndex((item) => {
      return (
        item.entityInfo.entityName === entityName &&
        item.entityInfo.entity.id === entityId &&
        (includeItemsWhichAreBeingUploaded || !item.isBeingUploaded)
      );
    });
  }

  private removeItem(
    item: QueueItem<
      TSynchronizationEnvironmentTypes,
      any,
      TEntityTypesByEntityName
    >
  ): void {
    const index = this.items.indexOf(item);
    if (index >= 0) {
      this.items.splice(index, 1);
    }
  }

  private findNextItemToDrain(): QueueItem<
    TSynchronizationEnvironmentTypes,
    any,
    TEntityTypesByEntityName
  > | null {
    return (
      this.items.find(
        (item) =>
          !item.failed &&
          item.entityInfo.entity.readyToSynchronize &&
          !item.isBeingUploaded
      ) ?? null
    );
  }

  private resetFailedItems(): void {
    for (const item of this.items) {
      item.failed = false;
    }
  }

  private resetItems(): void {
    for (const item of this.items) {
      item.failed = false;
      item.isBeingUploaded = false;
      item.needsReupload = false;
    }
  }

  private getOrderForEntityName(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  ): number {
    const index = this.orderedEntityNames.indexOf(entityName);
    if (index === -1) {
      throw new Error(`no order found for entityName "${entityName}"`);
    }

    return index;
  }

  private insertItem(
    item: QueueItem<
      TSynchronizationEnvironmentTypes,
      any,
      TEntityTypesByEntityName
    >
  ): void {
    const insertionIndex = this.items.findIndex((i) => i.order > item.order);
    if (insertionIndex >= 0) {
      this.items.splice(insertionIndex, 0, item);
    } else {
      this.items.push(item);
    }
  }
}

export enum QueueItemType {
  PUT = 'put',
  DELETE = 'delete'
}

type QueueItem<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  entityInfo: EntitySynchronizationInfo<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >;
  order: number;
  failed: boolean;
  isBeingUploaded: boolean;
  /**
   * if this is true, then the item has been modified while it was being uploaded and needs to be requeued after successfully uploading it
   */
  needsReupload: boolean;
};

export type EntitySynchronizationInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = EntityWithEntityName<
  TSynchronizationEnvironmentTypes,
  TEntityName,
  TEntityTypesByEntityName
> & {
  type: QueueItemType;
};

export type EntityToSynchronizeInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = {
  entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
  entityId: string;
  failed: boolean;
};

export type SynchronizationQueueUploadFunction<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  ReturnValue
> = (
  item: EntitySynchronizationInfo<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >
) => Promise<ReturnValue>;

/**
 * should return true if the item has been modified and false if it hasn't been
 */
export type ModifyFunction<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = (
  entityInfo: EntitySynchronizationInfo<
    TSynchronizationEnvironmentTypes,
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    TEntityTypesByEntityName
  >
) => boolean;
